import React from "react";
import {
    Box, Chip, Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import {BoatRaceResult} from "../../schemas/results/season/BoatRaceResut";
import {SeasonCategoryResults} from "../../schemas/results/season/SeasonCategoryResults";
import {Race} from "../../schemas/races/racesSchema";
import dayjs from "dayjs";
import useMediaQuery from "@mui/material/useMediaQuery";

interface AccordionRowCategoryProps {
    row: SeasonCategoryResults;
    races: Race[];
}

const AccordionRowCategory: React.FC<AccordionRowCategoryProps> = ({row, races}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const countedMap = new Map<number, BoatRaceResult>();
    const uncountedMap = new Map<number, BoatRaceResult>();
    row.raceResults.forEach(r => countedMap.set(r.race.id, r));
    row.uncountedRaceResults.forEach(r => uncountedMap.set(r.race.id, r));
    const sortedRaces = [...races].sort((a, b) => dayjs(a.date).isBefore(dayjs(b.date)) ? -1 : 1);

    const getPointsColor = (points: number, isUncounted: boolean) => {
        if (points === 0) return "gray";
        if (isUncounted) return "red";
        return "black";
    };

    return (
        <Paper elevation={isMobile ? 0 : 1} sx={{px: 1}}>
            <Box sx={{mt: 2, mb: 2}}>
                {isMobile && (
                    <Divider/>
                )}
                <Typography variant="h6" sx={{mb: 1, pt: 1}}>
                    Přehled bodů v jednotlivých závodech
                </Typography>
                <Divider/>
                {isMobile ? (
                    <Stack spacing={1}>
                        {sortedRaces.map(race => {
                            const counted = countedMap.get(race.id);
                            const uncounted = uncountedMap.get(race.id);
                            const result = counted ?? uncounted;
                            const points = result?.points ?? 0;
                            const isUncounted = !!uncounted;
                            const isDisqualified = !!result?.disqualified;
                            const pointsColor = getPointsColor(points, isUncounted);

                            return (
                                <Stack key={race.id} direction="row" justifyContent="space-between" alignItems="center"
                                       sx={{
                                           borderBottom: '1px solid #eee',
                                           py: 0.5,
                                       }}>
                                    <Stack>
                                        <Typography fontWeight="bold">{race.name}</Typography>
                                        <Typography variant="caption">
                                            {dayjs(race.date).format("DD.MM.YYYY")}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography fontWeight="bold" color={pointsColor}>
                                            {points}
                                        </Typography>
                                        {isDisqualified && (
                                            <Tooltip title="Diskvalifikován" arrow>
                                                <Chip label="DSQ" color="error" size="small" sx={{height: 18, fontSize: "0.65rem", fontWeight: "bold"}} />
                                            </Tooltip>
                                        )}
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                ) : (
                    <TableContainer sx={{overflowX: "auto"}}>
                        <Table size="small" sx={{minWidth: "100%"}}>
                            <TableHead>
                                <TableRow>
                                    {sortedRaces.map((race) => (
                                        <TableCell
                                            key={race.id}
                                            align="center"
                                        >
                                            <Stack direction="column" spacing={0} alignItems="center">
                                                <Typography fontWeight="bold">{race.name}</Typography>
                                                <Typography>{dayjs(race.date).format("DD.MM.YYYY")}</Typography>
                                            </Stack>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    {sortedRaces.map((race) => {
                                        const counted = countedMap.get(race.id);
                                        const uncounted = uncountedMap.get(race.id);
                                        const result = counted ?? uncounted;
                                        const points = result?.points ?? 0;
                                        const isUncounted = !!uncounted;
                                        const isDisqualified = !!result?.disqualified;
                                        const pointsColor = getPointsColor(points, isUncounted);

                                        return (
                                            <TableCell
                                                key={race.id}
                                                align="center"
                                            >
                                                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                                    <Typography fontWeight="bold" color={pointsColor}>
                                                        {points}
                                                    </Typography>
                                                    {isDisqualified && (
                                                        <Tooltip title="Diskvalifikován" arrow>
                                                            <Chip label="DSQ" color="error" size="small" sx={{height: 18, fontSize: "0.65rem", fontWeight: "bold"}} />
                                                        </Tooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {row.uncountedRacesCount > 0 && (
                    <Typography variant="caption" sx={{mt: 1, display: "block", color: "text.secondary"}}>
                        Červeně označené výsledky závodů nejsou započítány. Počet nezapočítaných
                        závodů {row.uncountedRacesCount}.
                        Nezapočítané body {row.uncountedRaceResults.reduce((acc, cur) => acc + cur.points, 0)}.
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};

export default AccordionRowCategory;
